import { createReducer } from "reduxsauce"
import { put, takeLatest } from "redux-saga/effects"
import { filter, findIndex, cloneDeep, concat, each, find, get } from "lodash"
import { v4 as uuid } from "uuid"
import actionCreator from "helpers/actionCreator"
import { FILTERS, NORMALIZATION, changeSegmentNormalization } from "helpers/segments"

export const { Types, Creators } = actionCreator("ompnt/modules/segmentsRoot", {
  addComparisonItem: {
    props: ["item", "parent", "trackType"],
    track: true,
    trackType: ({ trackType }) => trackType,
    trackPayload: {},
  },
  removeComparisonItem: {
    props: ["item", "trackType"],
    track: true,
    trackType: ({ trackType }) => trackType,
    trackPayload: {},
  },
  splitItem: {
    props: ["item", "trackType"],
    track: true,
    trackType: ({ trackType }) => trackType,
    trackPayload: {},
  },
  setSegmentNormalization: {
    props: ["item", "normalization", "trackType"],
    track: true,
    trackType: ({ trackType }) => trackType,
    trackPayload: {},
  },
  setComparisonItems: ["items"],
  share: ["items"],
  addInstrumentWithHedge: ["instrument"],
})

function handleRemoveComparisonItem(state, { item: { id, duplicate } }) {
  const comparisonItems = filter(state.comparisonItems, item => !(item.id === id && item.duplicate === duplicate))
  const hedges = filter(state.hedges, hedge => !((hedge.duplicate || hedge.id) === (duplicate || id)))
  const newState = { comparisonItems, hedges }

  if (state.selected && state.selected.id === id) {
    newState.selected = null
    newState.summaryPanelOpen = false
  }

  return { ...state, ...newState }
}

function handleAddComparisonItem(state, { item, parent }) {
  const { comparisonItems: notRefItems } = state
  const comparisonItems = [...notRefItems]

  const initialItem = find(comparisonItems, ({ initialItem, isRefInstrument }) => initialItem || isRefInstrument)

  // Add refInstrument to comparison items array for duplicate checking
  if (parent?.isRefInstrument) {
    comparisonItems.push({
      type: "security",
      id: `${parent.ticker}.${parent.mic}`,
      ...parent,
    })
  }
  const items = Array.isArray(item) ? item : [item]
  const toAdd = []
  each(items, (item) => {
    const newItem = {
      // ResearchTopic(only) provides baseTypes for its items because the item.type property gets mutated when running
      // the different item selectors. The value relevant to SegmentRoots is baseType.
      type: item.baseType || item.type,
      label: item.name || item.label,
      segment: item.segment,
      parent,
      duplicate: item.duplicate,
      fxHedgeEnabled: get(initialItem, "fxHedgeEnabled"),
    }

    if (item.type === "security" && !item.isComputedId) {
      if (item.exchange === "_SWP") {
        newItem.type = "swap"
        newItem.id = item.ticker
      } else {
        newItem.internalId = item.internalId
        newItem.id = `${item.ticker}.${item.mic || item.exchange}`
        newItem.ticker = item.ticker
        newItem.mic = item.mic || item.exchange
        newItem.assetClass = item.assetClass
        newItem.modelProviderId = item.modelProviderId
      }
    } else {
      newItem.id = item.id
    }

    // If an item has a duplicate AND a parent item, it means were splitting an item with a duplicateId and
    // we need to generate new ids for the split items
    if (!newItem.duplicate || (newItem.duplicate && newItem.parent)) {
      const duplicates = concat(comparisonItems, toAdd).some(({ id }) => id === newItem.id)
      if (duplicates) {
        newItem.duplicate = uuid()
      }
    }

    if (item.uploading) {
      newItem.uploading = !!item.uploading
    }
    toAdd.push(newItem)
  })

  return { ...state, comparisonItems: [...state.comparisonItems, ...toAdd] }
}

function handleSetSegmentNormalization(state, { item, normalization }) {
  const comparisonItems = cloneDeep(state.comparisonItems)
  const existingItemIndex = findIndex(comparisonItems, ({ id, duplicate }) => id === item.id && duplicate === item.duplicate)
  comparisonItems.splice(existingItemIndex, 1, changeSegmentNormalization(item, normalization))

  return { ...state, comparisonItems }
}

function handleSetComparisonItems(state, { items }) {
  if (!items) { return state }
  return { ...state, comparisonItems: items }
}

const DEFAULT_STATE = {
  comparisonItems: [],
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_COMPARISON_ITEMS]: handleSetComparisonItems,
  [Types.REMOVE_COMPARISON_ITEM]: handleRemoveComparisonItem,
  [Types.ADD_COMPARISON_ITEM]: handleAddComparisonItem,
  [Types.SET_SEGMENT_NORMALIZATION]: handleSetSegmentNormalization,
})

function* handleSplitItem({ item }) {
  const long = {
    ...item,
    segment: {
      filters: [{
        [FILTERS.LONG_SHORT]: "LONG",
      }],
      normalization: NORMALIZATION.CONTRIBUTION,
    },
  }
  const short = {
    ...item,
    segment: {
      filters: [{
        [FILTERS.LONG_SHORT]: "SHORT",
      }],
      normalization: NORMALIZATION.CONTRIBUTION,
    },
  }
  yield put(Creators.addComparisonItem([long, short], item))
}

export function* saga() {
  yield takeLatest(Types.SPLIT_ITEM, handleSplitItem)
}
